import { generateId, seedClock } from '../id.js'
import { extractLilodataBlock } from '../lilodata.js'
import type { Block, SpaceEntry } from '../types.js'
import { cloneSpaceEntry, decodeJsonSpacePayload, isDeletionReceipt, isLiveBlock } from './spaceCodec.js'

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'success' | 'error'

type SyncOptions = {
  debounceMs: number
  onSync: () => Promise<boolean>
  onStatus?: (status: SyncStatus, progress: number) => void
}

export type MergeResult = {
  blocks: SpaceEntry[]
  metaBlock?: Block
  mergedAll: SpaceEntry[]
}

export class SyncManager {
  private debounceMs: number
  private onSync: () => Promise<boolean>
  private onStatus?: (status: SyncStatus, progress: number) => void
  private timer?: number
  private progressTimer?: number
  private status: SyncStatus = 'idle'
  private dirty = false
  private revision = 0
  private epoch = 0
  private inFlight?: Promise<void>
  private lastMarkAt = 0
  private retryTimer?: number
  private retryAttempt = 0
  private readonly retryDelaysMs = [200, 400, 800, 1600]

  constructor(opts: SyncOptions) {
    this.debounceMs = opts.debounceMs
    this.onSync = opts.onSync
    this.onStatus = opts.onStatus
  }

  markDirty(): void {
    this.dirty = true
    this.revision += 1
    if (this.retryTimer) clearTimeout(this.retryTimer)
    this.retryTimer = undefined
    this.retryAttempt = 0
    this.lastMarkAt = Date.now()
    this.setStatus('pending', 0)
    this.startProgressLoop()
    if (this.timer) {
      clearTimeout(this.timer)
    }
    this.timer = window.setTimeout(() => {
      this.timer = undefined
      void this.sync()
    }, this.debounceMs)
  }

  async syncNow(): Promise<boolean> {
    if (!this.dirty) return true
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = undefined
    }
    await this.sync()
    return !this.dirty
  }

  isDirty(): boolean {
    return this.dirty
  }

  stop(): void {
    this.epoch += 1
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = undefined
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = undefined
    }
    this.retryAttempt = 0
    this.dirty = false
    this.setStatus('idle', 0)
  }

  private startProgressLoop(): void {
    if (this.progressTimer) return
    this.progressTimer = window.setInterval(() => {
      if (this.status !== 'pending') return
      const elapsed = Date.now() - this.lastMarkAt
      const fraction = Math.min(1, elapsed / this.debounceMs)
      this.setStatus('pending', fraction)
    }, 120)
  }

  private sync(): Promise<void> {
    if (this.inFlight) return this.inFlight
    if (!this.dirty) return Promise.resolve()
    const revision = this.revision
    const epoch = this.epoch
    const operation = (async () => {
      this.setStatus('syncing', 1)
      let ok = false
      try {
        ok = await this.onSync()
      } catch {
        // A failed attempt retains debt and uses the same retry path.
      }
      if (epoch !== this.epoch) return
      if (ok && revision === this.revision) {
        this.dirty = false
        this.retryAttempt = 0
        this.setStatus('success', 1)
      } else if (revision !== this.revision) {
        this.setStatus('pending', 0)
        this.startProgressLoop()
      } else {
        this.setStatus('error', 1)
      }
    })()
    this.inFlight = operation
    return operation.finally(() => {
      if (this.inFlight === operation) this.inFlight = undefined
      if (this.dirty && !this.timer) this.scheduleRetry()
    })
  }

  private scheduleRetry(): void {
    if (this.retryTimer || !this.dirty) return
    const delay = this.getRetryDelayMs()
    this.retryAttempt += 1
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined
      void this.sync()
    }, delay)
  }

  private getRetryDelayMs(): number {
    if (this.retryAttempt < this.retryDelaysMs.length) {
      return this.retryDelaysMs[this.retryAttempt]
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return 16000
    }
    return 4000
  }

  private setStatus(status: SyncStatus, progress: number): void {
    this.status = status
    if (this.onStatus) this.onStatus(status, progress)
    if ((status === 'idle' || status === 'success' || status === 'error') && this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = undefined
    }
  }
}

export function parseBlocksFromJson(json: string): SpaceEntry[] {
  return decodeJsonSpacePayload(json)
}

export function mergeBlocks(local: SpaceEntry[], remote: SpaceEntry[]): MergeResult {
  const merged = new Map<string, SpaceEntry>()
  const consider = (input: SpaceEntry) => {
    const block = cloneSpaceEntry(input)
    const existing = merged.get(block.ided[0])
    if (!existing || block.ided[1] > existing.ided[1]
      || (block.ided[1] === existing.ided[1] && isDeletionReceipt(block))) {
      merged.set(block.ided[0], block)
    }
  }
  local.forEach(consider)
  remote.forEach(consider)
  const mergedAll = [...merged.values()].sort((a, b) => a.ided[0].localeCompare(b.ided[0]))
  const { blocks, metaBlock } = extractLilodataBlock(mergedAll)
  return { blocks, metaBlock, mergedAll }
}

// A merge is a new space revision only when neither input already contains it.
export function mergeSpaceSnapshots(local: SpaceEntry[], remote: SpaceEntry[]): MergeResult {
  const merged = mergeBlocks(local, remote)
  const serialized = serializeBlocks(merged.mergedAll)
  if (merged.metaBlock && serialized !== serializeBlocks(local) && serialized !== serializeBlocks(remote)) {
    seedClock([...local, ...remote])
    merged.metaBlock.ided[1] = generateId()
  }
  return merged
}

export function serializeBlocks(blocks: SpaceEntry[]): string {
  const ordered = blocks
    .map(block => cloneSpaceEntry(block))
    .sort((a, b) => a.ided[0].localeCompare(b.ided[0]))
  return JSON.stringify(ordered)
}

export function hasMeaningfulContentBlocks(blocks: SpaceEntry[]): boolean {
  const { blocks: contentBlocks } = extractLilodataBlock(blocks)
  return contentBlocks.some((block) => {
    if (!isLiveBlock(block)) return false
    return block.text.trim().length > 0
  })
}

export function hasMeaningfulLocalDraft(input: {
  blocks: SpaceEntry[]
  isSeedOnly: boolean
}): boolean {
  if (input.isSeedOnly) return false
  return hasMeaningfulContentBlocks(input.blocks)
}

type CleanSeedMetadata = {
  published: boolean
  deleted_at: string | null
  dirty: boolean
  dirty_since: string | null
  sync_at: string | null
  last_sync_ok_at: string | null
  payload_state: 'known' | 'hydrated' | 'missing'
  payload_error_at: string | null
  in_flight_tab_id: string | null
  in_flight_started_at: string | null
}

export function hasCleanSeedMetadata(meta: CleanSeedMetadata): boolean {
  return !(
    meta.published
    || meta.deleted_at
    || meta.dirty
    || meta.dirty_since
    || meta.sync_at
    || meta.last_sync_ok_at
    || meta.payload_state !== 'hydrated'
    || meta.payload_error_at
    || meta.in_flight_tab_id
    || meta.in_flight_started_at
  )
}

export function isCleanSeedSpace(input: {
  meta: CleanSeedMetadata
  blocks: SpaceEntry[]
  isSeedOnly: boolean
}): boolean {
  if (!hasCleanSeedMetadata(input.meta)) return false
  return !hasMeaningfulLocalDraft({
    blocks: input.blocks,
    isSeedOnly: input.isSeedOnly
  })
}

export type LoginTransitionDecision =
  | 'create-first-cloud-space'
  | 'prompt-local-found'
  | 'open-latest-cloud'

export function classifyLoginTransition(input: {
  hasCloudSpaces: boolean
  hasMeaningfulLocalDraft: boolean
}): LoginTransitionDecision {
  if (!input.hasCloudSpaces) return 'create-first-cloud-space'
  if (input.hasMeaningfulLocalDraft) return 'prompt-local-found'
  return 'open-latest-cloud'
}
